import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Bell } from "lucide-react";
import { toast } from "react-toastify";

import type { AlertAction } from "@/types/action-groups";
import { apiService } from "@/lib/api-service";
import { useSession } from "@/hooks/use-session";

import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";

export const Route = createFileRoute("/_app/_/action-groups/alerts")({
	component: ActionGroupAlertsPage,
});

function formatDate(dateStr: string) {
	return new Date(dateStr).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

function ActionGroupAlertsPage() {
	const session = useSession();
	const campaignId = session.campaign?.id;
	const queryClient = useQueryClient();

	const { data, isLoading, error } = useQuery({
		queryKey: ["action-group-alerts", campaignId],
		queryFn: () => apiService.getAlerts(campaignId!),
		enabled: !!campaignId,
	});

	const resolveMutation = useMutation({
		mutationFn: ({
			alertId,
			action,
		}: {
			alertId: number;
			action: AlertAction;
		}) => apiService.resolveAlert(campaignId!, alertId, action),
		onSuccess: (_data, variables) => {
			const actionLabels: Record<AlertAction, string> = {
				confirm: "Removal confirmed",
				override: "Contact kept in group",
				dismiss: "Alert dismissed",
			};
			toast.success(actionLabels[variables.action]);

			queryClient.invalidateQueries({
				queryKey: ["action-group-alerts", campaignId],
			});
			queryClient.invalidateQueries({
				queryKey: ["action-group-alerts-count", campaignId],
			});
			queryClient.invalidateQueries({
				queryKey: ["action-groups", campaignId],
			});
		},
		onError: () => {
			toast.error("Failed to resolve alert. Please try again.");
		},
	});

	if (!campaignId) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-7xl space-y-6">
					<p className="text-muted-foreground">
						No campaign found. Please complete onboarding first.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-muted/30 p-6">
			<div className="mx-auto max-w-4xl space-y-6">
				{/* Back link */}
				<Button variant="ghost" size="sm" asChild>
					<Link to="/action-groups">
						<ArrowLeft data-icon="inline-start" className="size-4" />
						Back to Action Groups
					</Link>
				</Button>

				{/* Header */}
				<div>
					<h1 className="text-2xl font-semibold">Auto-Removal Alerts</h1>
					<p className="text-sm text-muted-foreground">
						Review contacts flagged for automatic removal from action
						groups
					</p>
				</div>

				{/* Loading */}
				{isLoading && (
					<div className="flex items-center justify-center py-20">
						<Spinner className="size-6" />
					</div>
				)}

				{/* Error */}
				{error && (
					<div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
						<p className="text-destructive">
							Failed to load alerts. Please try again.
						</p>
					</div>
				)}

				{/* Empty state */}
				{!isLoading && !error && data && data.alerts.length === 0 && (
					<div className="rounded-lg border border-dashed p-12 text-center">
						<Bell className="mx-auto mb-4 size-10 text-muted-foreground/50" />
						<h3 className="text-lg font-medium">No pending alerts</h3>
						<p className="mt-1 text-sm text-muted-foreground">
							There are no auto-removal alerts to review right now.
						</p>
					</div>
				)}

				{/* Alert cards */}
				{!isLoading &&
					!error &&
					data &&
					data.alerts.length > 0 && (
						<div className="space-y-4">
							{data.alerts.map((alert) => (
								<Card key={alert.id}>
									<CardHeader>
										<CardTitle>
											{alert.contact_name || "Unknown Contact"}
										</CardTitle>
										<CardDescription>
											Group: {alert.group_name}
										</CardDescription>
									</CardHeader>
									<CardContent>
										<div className="space-y-3">
											<div>
												<p className="text-sm font-medium text-muted-foreground">
													Reason
												</p>
												<p className="text-sm">
													{alert.reason}
												</p>
											</div>
											<p className="text-xs text-muted-foreground">
												Created {formatDate(alert.created_at)}
											</p>
											<div className="flex items-center gap-2 pt-2">
												<Button
													variant="destructive"
													size="sm"
													disabled={resolveMutation.isPending}
													onClick={() =>
														resolveMutation.mutate({
															alertId: alert.id,
															action: "confirm",
														})
													}
												>
													Confirm Removal
												</Button>
												<Button
													variant="outline"
													size="sm"
													disabled={resolveMutation.isPending}
													onClick={() =>
														resolveMutation.mutate({
															alertId: alert.id,
															action: "override",
														})
													}
												>
													Keep in Group
												</Button>
												<Button
													variant="ghost"
													size="sm"
													disabled={resolveMutation.isPending}
													onClick={() =>
														resolveMutation.mutate({
															alertId: alert.id,
															action: "dismiss",
														})
													}
												>
													Dismiss
												</Button>
											</div>
										</div>
									</CardContent>
								</Card>
							))}
						</div>
					)}
			</div>
		</div>
	);
}
